import {
  positionToDegrees,
  type FlagPose,
} from "@nav-games/semaphore";

interface Props {
  /// Pose to render. `null` renders the rest position (both arms
  /// straight down) — used for word breaks and the `?` placeholder.
  pose: FlagPose | null;
  /// SVG width/height in pixels. Defaults to 160. The drawing is
  /// scaled to fit, so any size works.
  size?: number;
  /// Optional label rendered below the figure (typically the letter).
  caption?: string;
}

/// Stick-figure semaphore signaller. The sender is drawn facing the
/// viewer, so arms named "right" in the pose appear on the viewer's
/// LEFT and vice versa — flipped here for natural visual reading.
///
/// Each red/yellow flag is the international maritime distress
/// flag pattern (diagonal split), drawn as a small triangle at the
/// far end of the arm.
export function SemaphoreFigure({ pose, size = 160, caption }: Props) {
  const w = size;
  const h = size;
  // Geometry — keep it simple. Body is a vertical line; shoulders are
  // a horizontal line one quarter from the top; arms emanate from the
  // shoulder ends. Flags hang at the far end.
  // Geometry pushed down by ~10% from a naive equal-spaced layout so
  // a flag held straight up (position 1) doesn't clip the top of the
  // SVG. arm-tip-y when up = shoulderY - armLength; the flag extends
  // another flagSize/2 above that, so shoulderY must be > armLength +
  // half-flag with a small margin.
  const cx = w / 2;
  const headRadius = w * 0.08;
  const headCy = h * 0.24;
  const shoulderY = h * 0.40;
  const shoulderSpan = w * 0.24; // distance between shoulder points
  const armLength = w * 0.30;
  const flagSize = w * 0.10;
  const senderRightShoulderX = cx - shoulderSpan / 2; // viewer's LEFT
  const senderLeftShoulderX = cx + shoulderSpan / 2; // viewer's RIGHT
  const hipY = h * 0.78;

  // For the sender's RIGHT arm:
  //   position 1 (up)        → arm points to 12 o'clock
  //   position 3 (right)     → arm points to viewer's left (3 o'clock from sender)
  //   position 5 (down)      → arm points to 6 o'clock
  //   position 7 (left)      → arm points to viewer's right (9 o'clock from sender)
  // The sender's-right arm rotates CLOCKWISE around its shoulder when
  // numbers increase (1 → 2 → 3 …). From the viewer, that means the
  // arm sweeps from 12 → viewer's-left → 6 → viewer's-right → 12.
  // The arm tip's offset from the shoulder, in viewer coordinates:
  //   dx_viewer = -sin(deg)  (negative because sender's-right == viewer's-left)
  //   dy        =  cos(deg) ... wait, let me redo this carefully.
  //
  // Easier mental model: position 1 (up) is at 0°. In SVG, y grows
  // downward. The arm vector from shoulder to flag for the SENDER's
  // RIGHT arm at position p (sender-clockwise angle d = (p-1)*45°):
  //   viewer_dx = -sin(d) * L    (sender's clockwise → viewer's
  //                                anti-clockwise on screen, so negate the x)
  //   viewer_dy = -cos(d) * L    (negative because up is -y in SVG)
  // For the SENDER's LEFT arm, mirror x:
  //   viewer_dx =  sin(d) * L
  //   viewer_dy = -cos(d) * L
  const rightPos = pose?.right ?? 5; // rest position
  const leftPos = pose?.left ?? 5;
  const rightDeg = positionToDegrees(rightPos);
  const leftDeg = positionToDegrees(leftPos);
  const rightRad = (rightDeg * Math.PI) / 180;
  const leftRad = (leftDeg * Math.PI) / 180;
  const rightTip = {
    x: senderRightShoulderX - Math.sin(rightRad) * armLength,
    y: shoulderY - Math.cos(rightRad) * armLength,
  };
  const leftTip = {
    x: senderLeftShoulderX + Math.sin(leftRad) * armLength,
    y: shoulderY - Math.cos(leftRad) * armLength,
  };

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      width={w}
      height={h}
      style={{ display: "block" }}
      aria-label={caption ? `Semaphore for ${caption}` : "Semaphore figure"}
    >
      {/* Head */}
      <circle
        cx={cx}
        cy={headCy}
        r={headRadius}
        fill="none"
        stroke="var(--text)"
        strokeWidth={2}
      />
      {/* Body */}
      <line
        x1={cx}
        y1={headCy + headRadius}
        x2={cx}
        y2={hipY}
        stroke="var(--text)"
        strokeWidth={2}
      />
      {/* Hips → legs (just suggest a stance) */}
      <line
        x1={cx}
        y1={hipY}
        x2={cx - shoulderSpan / 2}
        y2={h * 0.96}
        stroke="var(--text)"
        strokeWidth={2}
      />
      <line
        x1={cx}
        y1={hipY}
        x2={cx + shoulderSpan / 2}
        y2={h * 0.96}
        stroke="var(--text)"
        strokeWidth={2}
      />
      {/* Shoulders */}
      <line
        x1={senderRightShoulderX}
        y1={shoulderY}
        x2={senderLeftShoulderX}
        y2={shoulderY}
        stroke="var(--text)"
        strokeWidth={2}
      />
      {/* Sender's RIGHT arm (viewer's left) */}
      <line
        x1={senderRightShoulderX}
        y1={shoulderY}
        x2={rightTip.x}
        y2={rightTip.y}
        stroke="var(--text)"
        strokeWidth={3}
        strokeLinecap="round"
      />
      <Flag x={rightTip.x} y={rightTip.y} size={flagSize} />
      {/* Sender's LEFT arm (viewer's right) */}
      <line
        x1={senderLeftShoulderX}
        y1={shoulderY}
        x2={leftTip.x}
        y2={leftTip.y}
        stroke="var(--text)"
        strokeWidth={3}
        strokeLinecap="round"
      />
      <Flag x={leftTip.x} y={leftTip.y} size={flagSize} />
      {caption !== undefined && (
        <text
          x={cx}
          y={h - 4}
          textAnchor="middle"
          fontSize={12}
          fill="var(--text-muted)"
        >
          {caption}
        </text>
      )}
    </svg>
  );
}

/// International maritime semaphore flag — square, diagonal split,
/// red top-left + yellow bottom-right. Drawn as a small square at the
/// arm tip; doesn't try to simulate cloth physics.
function Flag({ x, y, size }: { x: number; y: number; size: number }) {
  const half = size / 2;
  return (
    <g>
      <polygon
        points={`${x - half},${y - half} ${x + half},${y - half} ${x - half},${y + half}`}
        fill="#dc2626"
      />
      <polygon
        points={`${x + half},${y - half} ${x + half},${y + half} ${x - half},${y + half}`}
        fill="#fbbf24"
      />
      <rect
        x={x - half}
        y={y - half}
        width={size}
        height={size}
        fill="none"
        stroke="var(--text)"
        strokeWidth={0.5}
      />
    </g>
  );
}
